import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { cardActionFlags, RECLASSIFY_BLOCKED, humanServerError, waitForCardRead } from "../lib/card-actions";

/**
 * Base-Template für die State-Aktionen der Karten-Detailseiten (2026-06-10,
 * Thomas): Stundenliste UND Diäten laden dieselbe Leiste — neue Kartentypen
 * bekommen die Standard-Funktionalität geschenkt, statt sie einzeln
 * nachzubauen ("ich muss es nicht einzeln finden").
 *
 * Enthält die KIND-AGNOSTISCHEN, state-getriebenen Aktionen:
 *   - calculated|validated:  ↩ Erneut bearbeiten (POST /:id/reopen, pro kind)
 *   - calculated:            📊 Diese Karte exportieren (→ state exported)
 *   - exported:              📊 Erneut exportieren (audit, kein State-Change)
 *   - calculated|exported:   ↻ Zurück zur Bearbeitung (kind-agnostischer
 *                            /api/cards/:id/revert-to-ocr-complete)
 *
 * Kind-SPEZIFISCHES (Speichern/Validieren mit eigenen Guards, Archivieren)
 * kommt als children — die Seite behält die Fach-Logik, die Leiste die Mechanik.
 * State-driven Footer-Prinzip: der User sieht NIE einen Knopf, den der Server
 * ablehnen würde.
 */
export interface CardDetailActionsProps {
  kind: "timesheet" | "expense";
  cardId: string;
  month: string;
  clientId?: string;
  state: string;
  busy: boolean;
  err?: string | null;
  setBusy: (b: boolean) => void;
  setErr: (e: string | null) => void;
  /** Karte nach einer State-Aktion neu laden — Seite aktualisiert ihren State. */
  onRefresh: () => Promise<void>;
  /** Read-only Views (Zulagenblatt/Excel) haben KEIN eigenes „Validieren" → die
   *  geerbte Leiste bietet inline „Übernehmen" (validate) an, damit ocr_complete
   *  kein Sackgassen-State ist (P8). Die editierbaren Views lassen es weg (eigene
   *  Validieren-Logik mit Quality-Gate). */
  allowInlineValidate?: boolean;
  /** Karte 591: Human-Quality-Gate vor dem Inline-„Übernehmen" — liefert der
   *  Aufrufer false, wird NICHT validiert (er zeigt seinen eigenen Hinweis).
   *  Muster: mandatory Markierung-prüfen der Stundenliste (2026-06-22). */
  beforeInlineValidate?: () => boolean;
  /** Karte 593: Grund, warum das Inline-„Übernehmen" GESPERRT ist (z. B.
   *  „Zuerst Mitarbeiter zuordnen"). Sichtbar-aber-disabled mit Tooltip statt
   *  verstecktem Knopf — der User sieht WARUM (Glass-Box; vorher: Knopf weg,
   *  Sackgasse ohne Erklärung). */
  inlineValidateDisabledReason?: string;
  /** Bug 3 (Thomas 24.07., dc292aac): Excel-Import-Karten haben KEINE PDF-
   *  Quelle — „Neu einlesen (OCR)" ergibt dort keinen Sinn (Hub lehnt mit
   *  409 ab; der Knopf wird gar nicht erst angeboten). */
  hideReOcr?: boolean;
  /** Unklare Seite (kind='unknown'): Die Typ-Entscheidung faellt oben im
   *  gelben Kasten — mit den Arten, die der MANDANT hat. Der Umsortieren-Link
   *  waere hier eine zweite, aermere Antwort auf dieselbe Frage: er kennt nur
   *  die Route, nicht die Art, und schlaegt bei Mandanten mit mehreren Arten je
   *  Route fehl (`doc_type_required`). Eine Frage, ein Ort. */
  hideReclassify?: boolean;
  children?: ReactNode;
}

const KIND_PATH: Record<CardDetailActionsProps["kind"], string> = {
  timesheet: "timesheets",
  expense: "expenses",
};

// Umsortieren (2026-06-15): manueller, IMMER verfügbarer Reclassify in den
// jeweils anderen Kartentyp — nicht nur bei automatisch erkanntem
// WRONG_FORM_TYPE. Deckt den Fall ab, dass der User selbst entscheidet, dass
// eine sauber erkannte Karte eigentlich der andere Typ ist (Anlassfall
// Ardelean f05c9b36). State-getrieben: nur in nicht-blockierten States sichtbar
// (spiegelt BLOCKED_STATES in den /reclassify-Endpoints).
const RECLASSIFY: Record<CardDetailActionsProps["kind"], { target: "timesheet" | "expense"; label: string; idKey: string; path: string; confirm: string }> = {
  timesheet: {
    target: "expense", label: "🧳 Als Diäten umsortieren", idKey: "expense_id", path: "expenses",
    confirm: "Diese Karte als Diäten-Abrechnung umsortieren? Sie wandert in die Diäten und wird dort neu ausgelesen.",
  },
  expense: {
    target: "timesheet", label: "🕐 Als Stundenliste umsortieren", idKey: "timesheet_id", path: "timesheets",
    confirm: "Diese Karte als Stundenliste umsortieren? Sie wandert in die Stundenlisten und wird dort neu ausgelesen.",
  },
};

/** Mandanten-richtige Umsortieren-Texte (Thomas 24.07., Karten a8b25e43/
 *  6f7f2a81): die RECLASSIFY-Defaults sind Bestandsmandanten-Sprache
 *  (Diäten/Stundenliste) — bei 3g heißt der kind-Flip aber Zulagenblatt ↔
 *  Fahrtenbuch. Der Ziel-NAME kommt aus der Mandanten-Typ-Registry (eindeutige
 *  routes_to-Route je kind, dasselbe Prinzip wie die Typ-Herleitung #523);
 *  ohne eindeutige Route bleibt der Default (Bestand unverändert). */
function reclassifyTexte(
  cfg: (typeof RECLASSIFY)["timesheet"],
  types: Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>,
): { label: string; confirm: string } {
  const ziele = types.filter((t) => t.routesTo === cfg.target);
  if (ziele.length !== 1) return { label: cfg.label, confirm: cfg.confirm };
  const name = ziele[0]!.label;
  return {
    label: `⇄ Als ${name} umsortieren`,
    confirm: `Diese Karte als ${name} umsortieren? Sie wird dort neu ausgelesen.`,
  };
}

export function CardDetailActions({
  kind, cardId, month, clientId, state, busy, err,
  setBusy, setErr, onRefresh, allowInlineValidate, beforeInlineValidate, hideReOcr,
  hideReclassify, inlineValidateDisabledReason, children,
}: CardDetailActionsProps) {
  const nav = useNavigate();

  // Mandanten-Typ-Registry für die Umsortieren-Texte (fail-soft: Default-Texte).
  const [docTypes, setDocTypes] = useState<Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>>([]);
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    api.uploads.docTypes(clientId)
      .then((r) => { if (!cancelled) setDocTypes(r.types); })
      .catch(() => { /* fail-soft */ });
    return () => { cancelled = true; };
  }, [clientId]);

  // Karte 593 (Usability dd9f3e58): nach „Übernehmen" steht die Karte auf
  // `validated`, der Worker rechnet sie SEKUNDEN später auf `calculated` —
  // vorher sah der User das erst nach Browser-Refresh. Solange die Karte in
  // diesem Durchgangs-Zustand ist, wird sie automatisch nachgeladen (2.5s-Takt,
  // Kappe 60s — danach greift der normale manuelle Refresh).
  const pollTicks = useRef(0);
  useEffect(() => {
    if (state !== "validated") { pollTicks.current = 0; return; }
    const t = setInterval(() => {
      pollTicks.current += 1;
      if (pollTicks.current > 24) { clearInterval(t); return; }
      void onRefresh();
    }, 2500);
    return () => clearInterval(t);
  }, [state, onRefresh]);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const reopen = () => {
    if (!confirm("Karte zurück auf 'OCR Fertig' setzen? Die Berechnung wird entfernt und beim nächsten Validieren neu erstellt.")) return;
    void run(async () => {
      const r = await fetch(
        new URL(`api/${KIND_PATH[kind]}/${cardId}/reopen`, document.baseURI).toString(),
        { method: "POST", credentials: "same-origin" },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      await onRefresh();
    });
  };

  const exportCard = () => {
    void run(async () => {
      await api.export.download(month, clientId, { cardId });
      // Karte ist nach Export im state=exported — neu laden.
      await onRefresh();
    });
  };

  const reExport = () => {
    void run(async () => {
      await api.export.download(month, clientId, { cardId, includeExported: true });
    });
  };

  const revert = () => {
    const stateLabel = state === "exported" ? "exportiert" : "berechnet";
    const ok = window.confirm(
      `Diese Karte zurück nach ocr_complete?\n\n` +
      `Aktueller State: ${stateLabel}. Beim Zurücksetzen:\n` +
      `• Karte wird wieder editierbar (Werte, Mitarbeiter-Zuordnung)\n` +
      `• Berechnung wird beim nächsten "Berechnen" überschrieben\n` +
      `• Export-Datei bleibt liegen — Re-Export überschreibt sie\n\n` +
      `Fortfahren?`,
    );
    if (!ok) return;
    void run(async () => {
      await api.cards.revertToOcrComplete(cardId);
      await onRefresh();
    });
  };

  // P8-Sackgassen-Fix: „Neu einlesen (OCR)" = start-ocr (kind-spezifisch via
  // KIND_PATH). start-ocr existierte bisher NUR am Board → read-only Views (Zulagen-
  // blatt) waren ab ocr_complete/pending_review Sackgassen.
  const neuEinlesen = () => {
    if (!confirm("Diese Karte neu einlesen (OCR)? Der aktuelle Lesestand wird durch einen frischen OCR-Lauf ersetzt.")) return;
    void run(async () => {
      const r = await fetch(
        new URL(`api/${KIND_PATH[kind]}/${cardId}/start-ocr`, document.baseURI).toString(),
        { method: "POST", credentials: "same-origin" },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      // Anlassfall Thomas 23.07.: Status blieb auf ocr_pending stehen, bis
      // manuell refresht wurde. Jetzt: sofort frisch (zeigt ehrlich "liest"),
      // dann auf das Ende der Lesung warten und EINMAL final aktualisieren.
      await onRefresh();
      await waitForCardRead(kind, cardId);
      await onRefresh();
    });
  };

  // „Übernehmen (berechnen)" = validate → validated → calculated. Nur read-only
  // Views (allowInlineValidate); die editierbaren Views haben ihr eigenes Validieren.
  const uebernehmen = () => {
    if (beforeInlineValidate && !beforeInlineValidate()) return;
    void run(async () => {
      const r = await fetch(
        new URL(`api/${KIND_PATH[kind]}/${cardId}/validate`, document.baseURI).toString(),
        { method: "POST", credentials: "same-origin" },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      await onRefresh();
    });
  };

  const reclassify = () => {
    const cfg = RECLASSIFY[kind];
    const texte = reclassifyTexte(cfg, docTypes);
    if (!confirm(texte.confirm)) return;
    void run(async () => {
      const r = await fetch(
        new URL(`api/${KIND_PATH[kind]}/${cardId}/reclassify`, document.baseURI).toString(),
        { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ target: cfg.target }) },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      const body = await r.json().catch(() => ({}));
      const newId = body[cfg.idKey];
      if (newId) nav(`/${cfg.path}/${newId}`);
      else await onRefresh();
    });
  };

  const f = cardActionFlags(state, { allowInlineValidate });
  return (
    <div className="ts-detail-footer">
      {err && <span className="error">{err}</span>}
      {/* DER GRUND STEHT NEBEN DEM KNOPF, NICHT IM TOOLTIP.
       *
       *  ANLASSFALL 17.08.2026 (Franka, 08:08): „ich kann die beiden Blätter
       *  allerdings nicht validieren, da dieses Feld ausgegraut ist." Die Sperre
       *  war richtig (Überstundenpauschale nicht hinterlegt, Karte 725) — nur
       *  stand ihre Begründung im `title`-Attribut und erschien erst beim
       *  Darüberfahren mit der Maus. Sie sah einen grauen Knopf, sonst nichts,
       *  und musste eine Mail schreiben, um den Grund zu erfahren.
       *
       *  Das verletzte genau das Prinzip, das ueber dieser Datei steht: „der User
       *  sieht NIE einen Knopf, den der Server ablehnt". Wir haben den Knopf
       *  gezeigt und die Begruendung versteckt — die halbe Miete ist keine.
       *
       *  Gilt fuer JEDEN Grund, nicht nur die Pauschale: „Zuerst Mitarbeiter
       *  zuordnen" (Karte 593) hatte denselben Fehler. Der Tooltip bleibt
       *  zusaetzlich am Knopf. */}
      {f.uebernehmen && inlineValidateDisabledReason && (
        <span
          role="status"
          style={{
            flex: "1 1 100%", order: -1, background: "#fffbeb",
            border: "1px solid #f59e0b", borderLeft: "4px solid #f59e0b",
            borderRadius: 4, padding: "8px 12px", fontSize: 13, color: "#92400e",
            textAlign: "left", marginBottom: 8,
          }}
        >
          <strong>Validieren ist gesperrt:</strong> {inlineValidateDisabledReason}
        </span>
      )}
      {f.reopen && (
        <button
          type="button"
          className="link"
          onClick={reopen}
          disabled={busy}
          title="Karte zurück auf 'OCR Fertig' — danach wieder editierbar; Berechnung wird beim nächsten Validieren neu erstellt"
        >
          ↩ Erneut bearbeiten (zurück zu OCR Fertig)
        </button>
      )}
      {f.exportCard && (
        <button
          type="button"
          className="primary"
          onClick={exportCard}
          disabled={busy}
          title="Diese Karte als RZL-CSV exportieren — setzt state auf exported"
        >
          📊 Diese Karte exportieren
        </button>
      )}
      {f.reExport && (
        <button
          type="button"
          className="link"
          onClick={reExport}
          disabled={busy}
          title="Karte ist schon exportiert — CSV erneut generieren (kein State-Change)"
        >
          📊 Erneut exportieren
        </button>
      )}
      {f.revert && (
        <button
          type="button"
          className="link"
          onClick={revert}
          disabled={busy}
          title="Karte zurück nach ocr_complete — für Korrekturen + Re-Berechnung"
        >
          ↻ Zurück zur Bearbeitung
        </button>
      )}
      {f.neuEinlesen && !hideReOcr && (
        <button
          type="button"
          className="link"
          onClick={neuEinlesen}
          disabled={busy}
          title="Karte neu einlesen (OCR) — frischer Lesestand; ersetzt den aktuellen"
        >
          ↻ Neu einlesen (OCR)
        </button>
      )}
      {children}
      {/* UX-Angleich (Thomas 24.07.): Validieren sitzt bei ALLEN Typen an
          derselben Stelle — als GRUENE Abschluss-Aktion NACH den Seiten-
          Aktionen (Speichern etc.), vor dem Umsortieren-Link. Vorher stand
          der geerbte Knopf ganz links und blau (Zulagenblatt/Excel). */}
      {f.uebernehmen && (
        <button
          type="button"
          className="primary success"
          onClick={uebernehmen}
          disabled={busy || !!inlineValidateDisabledReason}
          title={inlineValidateDisabledReason
            ?? "Gelesene Werte validieren und berechnen — Karte geht auf 'berechnet'"}
        >
          Validieren
        </button>
      )}
      {f.reclassify && !hideReclassify && (
        <button
          type="button"
          className="link"
          onClick={reclassify}
          disabled={busy}
          title="Karte in den jeweils anderen Typ umsortieren — sie wird dort neu ausgelesen"
        >
          {reclassifyTexte(RECLASSIFY[kind], docTypes).label}
        </button>
      )}
    </div>
  );
}
